import { memo, useCallback, useState } from 'react';
import { withNamespaces } from 'react-i18next';
import { Button, Card, CardBody, CardTitle, Table } from 'reactstrap';

import RedFlagIcon from 'src/modules/3rd-party-management/components/RedFlagIcon';
import RiskLevelBadge from '../shared/RiskLevelBadge';
import CreateRiskAndTaskModal from './components/CreateRiskAndTaskModal';
import useCreatePackagingProductRiskAndTask from './hooks/useCreatePackagingProductRiskAndTask';
import useSupplierOverviewTopPackagingProducts from './hooks/useSupplierOverviewTopPackagingProducts';

const TopPackagingProductsSkeleton = () => (
    <Card className="overview-top-packaging-products h-100 mb-0">
        <CardBody>
            <div className="dt-field dt-skeleton mb-4" style={{ width: '35%' }} />
            <div className="dt-field dt-skeleton" style={{ width: '100%', height: '240px' }} />
        </CardBody>
    </Card>
);

const TopPackagingProducts = ({
    t,
    supplierId,
}) => {
    const [selectedProduct, setSelectedProduct] = useState(null);

    const fetchTopPackagingProductsQuery = useSupplierOverviewTopPackagingProducts({
        supplierId,
        t,
    });

    const createRiskAndTaskMutation = useCreatePackagingProductRiskAndTask({
        supplierId,
        t,
        onSuccess: () => {
            setSelectedProduct(null);
        },
    });

    const products = fetchTopPackagingProductsQuery.data?.products || [];

    const handleOpenModal = useCallback((product) => {
        setSelectedProduct(product);
    }, []);

    const handleCloseModal = useCallback(() => {
        if (createRiskAndTaskMutation.isLoading) {
            return;
        }

        setSelectedProduct(null);
    }, [createRiskAndTaskMutation.isLoading]);

    const handleConfirmCreate = useCallback(() => {
        if (!selectedProduct?.id) {
            return;
        }

        createRiskAndTaskMutation.mutate({
            productId: selectedProduct.id,
        });
    }, [createRiskAndTaskMutation, selectedProduct]);

    if (fetchTopPackagingProductsQuery.isLoading || fetchTopPackagingProductsQuery.isFetching) {
        return <TopPackagingProductsSkeleton />;
    }

    return (
        <>
            <Card className="overview-top-packaging-products h-100 mb-0">
                <CardBody>
                    <CardTitle tag="h6" className="overview-section-title mb-4">
                        {t('Top 5 Packaging Products Supplied')}
                    </CardTitle>

                    <div className="table-responsive overview-top-packaging-products__table-wrapper">
                        {!products.length ? (
                            <div className="text-center py-4 text-muted">
                                {t('No packaging products available.')}
                            </div>
                        ) : (
                            <Table className="overview-top-packaging-products__table mb-0" responsive>
                                <thead>
                                    <tr>
                                        <th>{t('Art Number')}</th>
                                        <th>{t('Product')}</th>
                                        <th>{t('Material')}</th>
                                        <th className="overview-top-packaging-products__status-header">
                                            {t('Status')}
                                        </th>
                                        <th>{t('Recommended Risk')}</th>
                                        <th>{t('Recommended Task')}</th>
                                        <th className="overview-top-packaging-products__action-header">
                                            {t('Action')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((product) => (
                                        <tr key={product.id}>
                                            <td>{product.artNumber || '—'}</td>
                                            <td>{product.product || '—'}</td>
                                            <td>{product.material || '—'}</td>
                                            <td className="overview-top-packaging-products__status-cell">
                                                <RedFlagIcon isFlagged={!!product.redFlag} />
                                            </td>
                                            <td>
                                                <div className="overview-top-packaging-products__recommended-risk">
                                                    <span className="overview-top-packaging-products__recommended-risk-title">
                                                        {product.recommendedRisk || '—'}
                                                    </span>
                                                    {product.recommendedRiskLevel && (
                                                        <RiskLevelBadge riskLevel={product.recommendedRiskLevel} />
                                                    )}
                                                </div>
                                            </td>
                                            <td>{product.recommendedTask || '—'}</td>
                                            <td>
                                                <Button
                                                    color="primary"
                                                    outline
                                                    size="sm"
                                                    type="button"
                                                    className="overview-top-packaging-products__action-btn"
                                                    onClick={() => handleOpenModal(product)}
                                                >
                                                    {t('Create Risk & Task')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </div>
                </CardBody>
            </Card>

            <CreateRiskAndTaskModal
                isOpen={!!selectedProduct}
                product={selectedProduct}
                isSubmitting={createRiskAndTaskMutation.isLoading}
                onConfirm={handleConfirmCreate}
                onCancel={handleCloseModal}
            />
        </>
    );
};

export default withNamespaces()(memo(TopPackagingProducts));
